import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { shoppingCartApi, AddToCartInput, UpdateCartItemInput } from '../api/shoppingCart';
import { useToast } from '../components/ui/Toast';

export const shoppingCartKeys = {
  all: ['shoppingCart'] as const,
  items: () => [...shoppingCartKeys.all, 'items'] as const,
};

export function useShoppingCart() {
  return useQuery({
    queryKey: shoppingCartKeys.items(),
    queryFn: () => shoppingCartApi.get(),
  });
}

export function useAddToCart() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (items: AddToCartInput[]) => shoppingCartApi.addItems(items),
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: shoppingCartKeys.all });
      const count = data.count;
      showToast(`Added to shopping list (${count} item${count !== 1 ? 's' : ''} total)`, 'success');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to add items to cart', 'error');
    },
  });
}

export function useUpdateCartItem() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: ({ itemId, data }: { itemId: string; data: UpdateCartItemInput }) =>
      shoppingCartApi.updateItem(itemId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: shoppingCartKeys.all });
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to update cart item', 'error');
    },
  });
}

export function useRemoveCartItem() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (itemId: string) => shoppingCartApi.removeItem(itemId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: shoppingCartKeys.all });
      showToast('Item removed from shopping list', 'success');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to remove item', 'error');
    },
  });
}

export function useClearCart() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: () => shoppingCartApi.clear(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: shoppingCartKeys.all });
      showToast('Shopping list cleared', 'success');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to clear shopping list', 'error');
    },
  });
}
